import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { errMsg } from "../../lib/api";

export const BUSINESS_TYPES = { retail: "Shop / retail", restaurant: "Restaurant / café / food", salon_spa: "Salon / spa / beauty", clinic_healthcare: "Clinic / healthcare",
  professional_services: "Professional services", education: "Education / coaching", real_estate: "Real estate", hospitality: "Hotel / hospitality", fitness: "Fitness / gym",
  nonprofit: "Non-profit / charity", manufacturing: "Manufacturing / B2B supply", technology: "Technology / software", creative_portfolio: "Creative / portfolio", events: "Events", other: "Other" };

const FLAG_LABELS = {
  sells_online: "Sell products online", catalogue_only: "Show products with an enquiry button", takes_bookings: "Online bookings / appointments",
  accepts_payments: "Accept online payments", multi_location: "More than one branch", has_physical_location: "Customers visit a physical place",
  needs_accounts: "Customer logins", needs_portal: "Private client portal", has_blog: "Articles / news", multilingual: "More than one language",
  needs_whatsapp: "WhatsApp contact", wants_ai: "AI assistant for visitors", existing_website: "Already have a website", b2b: "Sell to other businesses",
  events: "Run events", memberships: "Memberships", courses: "Online courses", donations: "Collect donations", portfolio: "Show past work",
  subscriptions: "Subscriptions", needs_quote_requests: "Customers ask for quotes", newsletter: "Send newsletters", digital_products: "Sell digital downloads",
  international_audience: "International customers", food_ordering: "Online food ordering", real_estate_listings: "Property listings", job_listings: "Job listings",
};

const listStr = (v) => (Array.isArray(v) ? v.join(", ") : v || "");

export default function StepProfile({ project, api, reload, go }) {
  const p0 = project.profile || {};
  const [p, setP] = useState({ ...p0, products_services: listStr(p0.products_services), objectives: listStr(p0.objectives), customer_actions: listStr(p0.customer_actions) });
  const [flags, setFlags] = useState(p0.flags || {});
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setP({ ...p, [k]: e.target.value });
  const save = async (confirm) => {
    setBusy(true);
    try {
      const split = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);
      await api.put(`/projects/${project.id}/profile`, { profile: { ...p, products_services: split(p.products_services), objectives: split(p.objectives), customer_actions: split(p.customer_actions), flags } });
      if (confirm) { await api.post(`/projects/${project.id}/confirm-profile`); await reload(); go(3); }
      else { await reload(); toast.success("Saved"); }
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const inferred = new Set(p0.ai_inferred_flags || []);
  const F = (k, label, hint, area) => (
    <div className="space-y-1.5"><Label htmlFor={`pf-${k}`}>{label}</Label>
      {area ? <Textarea id={`pf-${k}`} rows={3} value={p[k] || ""} onChange={set(k)} data-testid={`profile-${k}`} /> : <Input id={`pf-${k}`} value={p[k] || ""} onChange={set(k)} data-testid={`profile-${k}`} />}
      {hint && <p className="text-xs text-slate-500">{hint}</p>}</div>);
  return (
    <div className="grid lg:grid-cols-12 gap-8">
      <div className="lg:col-span-7 card-lux !p-8 space-y-5">
        <div><p className="eyebrow">Step 3</p><h2 className="mt-2 text-3xl text-[#062a20]">Is this right?</h2>
          <p className="text-sm text-slate-600 mt-1">Here's what we understood. Edit anything — we'll only use it after you confirm.</p></div>
        <div className="grid sm:grid-cols-2 gap-4">
          {F("business_name", "Business name")}
          <div className="space-y-1.5"><Label htmlFor="pf-type">Type of business</Label>
            <select id="pf-type" value={p.business_type || ""} onChange={set("business_type")} className="w-full h-10 rounded-md border px-3 text-sm bg-white" data-testid="profile-business_type">
              <option value="">Choose…</option>{Object.entries(BUSINESS_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
          {F("industry", "Industry")}{F("location", "Based in", "Only what you told us — we don't guess.")}
          {F("target_audience", "Your customers")}{F("target_geography", "Where your customers are")}
        </div>
        {F("products_services", "Products / services", "Separate with commas.", true)}
        {F("objectives", "What the website should achieve", "Separate with commas.", true)}
        {F("customer_actions", "What visitors should do", "e.g. call, book, buy, enquire.", true)}
      </div>
      <div className="lg:col-span-5 space-y-5">
        <div className="card-lux !p-6">
          <h3 className="text-xl text-[#062a20]">Your website needs</h3>
          <p className="text-xs text-slate-500 mt-1">These switches decide which features become required. Items marked "suggested" were picked up from your description.</p>
          <div className="mt-4 space-y-3 max-h-[420px] overflow-auto pr-1">
            {Object.entries(FLAG_LABELS).map(([k, l]) => (
              <div key={k} className="flex items-center justify-between gap-3">
                <Label htmlFor={`flag-${k}`} className="text-sm font-normal">{l}{inferred.has(k) && <span className="ml-2 text-[10px] uppercase tracking-wider text-[#9a7322]">suggested</span>}</Label>
                <Switch id={`flag-${k}`} checked={!!flags[k]} onCheckedChange={(v) => setFlags({ ...flags, [k]: v })} data-testid={`flag-${k}`} />
              </div>))}
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => save(true)} disabled={busy} className="btn-emerald" data-testid="profile-confirm">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Confirm & see recommendations</button>
          <button onClick={() => save(false)} disabled={busy} className="text-sm underline text-slate-600" data-testid="profile-save">Save without confirming</button>
        </div>
      </div>
    </div>
  );
}
